import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { usePatient, useUpdatePatient, useDeletePatient } from '../../hooks/usePatients';
import { useVisitsByPatientId, useVisit, useCreateVisit } from '../../hooks/useVisits';
import { usePrescription } from '../../hooks/usePrescriptions';
import { PageHeader } from '../../components/common/PageHeader';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { Modal } from '../../components/common/Modal';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { PatientForm } from '../../components/patients/PatientForm';
import { ImageGallery } from '../../components/visits/ImageGallery';
import { PrescriptionsList } from '../../components/prescriptions/PrescriptionsList';
import { PrescriptionItem } from '../../components/prescriptions/PrescriptionItem';
import { Calendar, FileText, Image, User, Plus, Stethoscope, Edit, Trash2, Hash, Pill, FlaskConical, Scan, Eye } from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';
import type { UpdatePatientDto } from '../../types/patient.types';

export const PatientProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const patientId = id ? parseInt(id, 10) : 0;

  const { data: patient, isLoading: isLoadingPatient, error: patientError } = usePatient(patientId);
  const { data: visits, isLoading: isLoadingVisits } = useVisitsByPatientId(patientId);

  // Filter out duplicate visits by ID
  const uniqueVisits = React.useMemo(() => {
    if (!visits) return [];
    const seen = new Set<number>();
    return visits.filter(visit => {
      if (seen.has(visit.id)) {
        return false;
      }
      seen.add(visit.id);
      return true;
    });
  }, [visits]);
  const createVisitMutation = useCreateVisit();
  const updatePatientMutation = useUpdatePatient();
  const deletePatientMutation = useDeletePatient();
  const [selectedVisitId, setSelectedVisitId] = React.useState<number | null>(null);
  const { data: selectedVisit, isLoading: isLoadingSelectedVisit } = useVisit(selectedVisitId || 0);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = React.useState<number | null>(null);
  const { data: selectedPrescription, isLoading: isLoadingPrescription } = usePrescription(selectedPrescriptionId || 0);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const handleQuickAddPrescription = async () => {
    if (!patient) return;

    try {
      // Check if there's a recent visit we can use (within last 24 hours)
      const recentVisit = uniqueVisits?.find(v => {
        const visitDate = new Date(v.visitDate);
        const now = new Date();
        const hoursDiff = (now.getTime() - visitDate.getTime()) / (1000 * 60 * 60);
        return hoursDiff <= 24;
      });

      if (recentVisit) {
        // Use existing recent visit
        navigate(`/prescriptions/new?visitId=${recentVisit.id}`);
        return;
      }

      // Create a new visit with minimal data (required for prescription)
      // Add a note to indicate this is for prescription only
      const prescriptionOnlyNote = t('visits.prescriptionOnlyVisit', 'زيارة للوصفة الطبية فقط') || 'زيارة للوصفة الطبية فقط';
      const trimmedNote = prescriptionOnlyNote.trim();
      if (!trimmedNote) {
        throw new Error('Translation for prescriptionOnlyVisit is missing or empty');
      }
      const visit = await createVisitMutation.mutateAsync({
        patientId: patient.id,
        visitDate: new Date().toISOString().split('T')[0],
        diagnosis: undefined,
        notes: trimmedNote, // This note ensures the visit is not empty
        files: [],
      });

      toast.success(t('visits.visitCreated', 'Visit created successfully'));
      navigate(`/prescriptions/new?visitId=${visit.id}`);
    } catch (error) {
      toast.error(t('visits.visitCreatedError', 'Failed to create visit. Please try again.'));
      console.error('Error creating visit:', error);
    }
  };

  const handleUpdatePatient = async (data: UpdatePatientDto) => {
    if (!patient) return;

    try {
      await updatePatientMutation.mutateAsync({ id: patient.id, data });
      toast.success(t('patients.patientUpdated', 'Patient updated successfully'));
      setIsEditModalOpen(false);
    } catch (error) {
      toast.error(t('patients.updateFailed', 'Failed to update patient'));
      console.error('Error updating patient:', error);
    }
  };

  const handleDeletePatient = async () => {
    if (!patient) return;

    try {
      await deletePatientMutation.mutateAsync(patient.id);
      toast.success(t('patients.patientDeleted', 'Patient deleted successfully'));
      navigate('/patients');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : t('patients.deleteFailed', 'Failed to delete patient');
      toast.error(errorMessage);
      console.error('Error deleting patient:', error);
    }
  };

  if (isLoadingPatient) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (patientError || !patient) {
    return <ErrorMessage message={patientError?.message || t('errors.required')} />;
  }

  return (
    <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        title={patient.name}
        subtitle={
          <div className={`d-flex align-items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className="text-muted">{t('patients.patientId')}</span>
            <span
              className="badge bg-primary d-inline-flex align-items-center gap-1"
              style={{
                fontSize: '0.875rem',
                padding: '0.375rem 0.75rem',
                fontWeight: 500,
              }}
            >
              <Hash size={14} />
              {patient.id}
            </span>
          </div>
        }
        actions={
          <div className={`d-flex gap-2 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button
              variant="primary"
              onClick={handleQuickAddPrescription}
              isLoading={createVisitMutation.isPending}
            >
              {isRTL ? (
                <>
                  <Plus className="ms-2" size={16} />
                  {t('common.newPrescription')} ({t('common.quick', 'Quick')})
                </>
              ) : (
                <>
                  <Plus className="me-2" size={16} />
                  {t('common.newPrescription')} ({t('common.quick', 'Quick')})
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(`/visits/new?patientId=${patient.id}`)}
            >
              {isRTL ? (
                <>
                  <Calendar className="ms-2" size={16} />
                  {t('common.newVisit')}
                </>
              ) : (
                <>
                  <Calendar className="me-2" size={16} />
                  {t('common.newVisit')}
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(`/patients/${patient.id}/visit-images`)}
            >
              {isRTL ? (
                <>
                  <Image className="ms-2" size={16} />
                  {t('visits.visitDetails')}
                </>
              ) : (
                <>
                  <Image className="me-2" size={16} />
                  {t('visits.visitDetails')}
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsEditModalOpen(true)}
            >
              {isRTL ? (
                <>
                  <Edit className="ms-2" size={16} />
                  {t('common.edit', 'Edit')}
                </>
              ) : (
                <>
                  <Edit className="me-2" size={16} />
                  {t('common.edit', 'Edit')}
                </>
              )}
            </Button>
            <Button
              variant="danger"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              {isRTL ? (
                <>
                  <Trash2 className="ms-2" size={16} />
                  {t('common.delete', 'Delete')}
                </>
              ) : (
                <>
                  <Trash2 className="me-2" size={16} />
                  {t('common.delete', 'Delete')}
                </>
              )}
            </Button>
          </div>
        }
      />

      <div className="row g-4">
        {/* Patient Information Card */}
        <div className="col-12 col-lg-8">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-bottom">
              <h5 className="card-title mb-0 fw-bold">{t('patients.patientDetails')}</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <User className={`text-primary ${isRTL ? 'ms-3' : 'me-3'}`} size={20} />
                    <div>
                      <p className="text-muted small mb-0">{t('patients.patientName')}</p>
                      <p className="mb-0 fw-semibold">{patient.name}</p>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <Calendar className={`text-primary ${isRTL ? 'ms-3' : 'me-3'}`} size={20} />
                    <div>
                      <p className="text-muted small mb-0">{t('patients.age')}</p>
                      <p className="mb-0 fw-semibold">{patient.age} {t('patients.years')}</p>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <i className={`bi bi-telephone text-primary ${isRTL ? 'ms-3' : 'me-3'}`} style={{ fontSize: '20px' }}></i>
                    <div>
                      <p className="text-muted small mb-0">{t('patients.phone')}</p>
                      <p className="mb-0 fw-semibold">{patient.phone}</p>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <i className={`bi bi-gender-ambiguous text-primary ${isRTL ? 'ms-3' : 'me-3'}`} style={{ fontSize: '20px' }}></i>
                    <div>
                      <p className="text-muted small mb-0">{t('patients.gender')}</p>
                      <p className="mb-0 fw-semibold">
                        {patient.gender === 'Male' ? t('patients.male') : 
                         patient.gender === 'Female' ? t('patients.female') : 
                         patient.gender}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Visit History */}
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-bottom">
              <h5 className="card-title mb-0 fw-bold">{t('visits.title')}</h5>
            </div>
            <div className="card-body">
              {isLoadingVisits ? (
                <div className="d-flex justify-content-center py-4">
                  <LoadingSpinner />
                </div>
              ) : uniqueVisits && uniqueVisits.length > 0 ? (
                <div className="list-group list-group-flush">
                  {uniqueVisits.map((visit) => (
                    <div
                      key={visit.id}
                      className="list-group-item list-group-item-action border-0 border-bottom cursor-pointer p-3"
                      onClick={() => setSelectedVisitId(visit.id)}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div className={`d-flex align-items-start justify-content-between gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="flex-grow-1">
                          <div className={`d-flex align-items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Calendar className="text-primary" size={18} />
                            <p className="mb-0 fw-semibold text-dark">{formatDate(visit.visitDate)}</p>
                          </div>
                          
                          {visit.diagnosis && (
                            <div className={`d-flex align-items-start gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Stethoscope className="text-info mt-1" size={16} />
                              <div className="flex-grow-1">
                                <p className="text-muted small mb-1">{t('prescriptions.diagnosis')}</p>
                                <p className="mb-0 text-dark">{visit.diagnosis}</p>
                              </div>
                            </div>
                          )}
                          
                          {visit.notes && (
                            <div className={`d-flex align-items-start gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <FileText className="text-secondary mt-1" size={16} />
                              <div className="flex-grow-1">
                                <p className="text-muted small mb-1">{t('prescriptions.notes')}</p>
                                <p className="mb-0 text-dark small">{visit.notes}</p>
                              </div>
                            </div>
                          )}
                          
                          {visit.files && visit.files.length > 0 && (
                            <div className={`d-flex align-items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Image className="text-success" size={16} />
                              <p className="mb-0 text-muted small">
                                {visit.files.length} {visit.files.length > 1 ? t('visits.images') : t('visits.image')}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className={`d-flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Button
                            variant="text"
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              setSelectedVisitId(visit.id);
                            }}
                            className="text-info p-2"
                            title={t('visits.viewDetails', 'عرض التفاصيل')}
                          >
                            <Eye size={18} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-center py-4 mb-0">{t('common.noData')}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Visit Details Modal */}
      <Modal
        isOpen={selectedVisitId !== null}
        onClose={() => setSelectedVisitId(null)}
        title={selectedVisit ? `${t('visits.visitDetails')} - ${formatDate(selectedVisit.visitDate)}` : t('visits.visitDetails')}
        size="xl"
      >
        {isLoadingSelectedVisit ? (
          <div className="d-flex justify-content-center py-5">
            <LoadingSpinner />
          </div>
        ) : selectedVisit ? (
          <div className="space-y-4">
            {/* Visit Information */}
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="fw-bold mb-3">{t('visits.visitInformation')}</h5>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <p className="text-muted small mb-1">{t('patients.patientName')}</p>
                    <p className="mb-0 fw-semibold">{selectedVisit.patientName}</p>
                  </div>
                  <div className="col-12 col-md-6">
                    <p className="text-muted small mb-1">{t('visits.visitDate')}</p>
                    <p className="mb-0 fw-semibold">{formatDate(selectedVisit.visitDate)}</p>
                  </div>
                  {selectedVisit.diagnosis && (
                    <div className="col-12">
                      <p className="text-muted small mb-1">{t('prescriptions.diagnosis')}</p>
                      <p className="mb-0 fw-semibold">{selectedVisit.diagnosis}</p>
                    </div>
                  )}
                  {selectedVisit.notes && (
                    <div className="col-12">
                      <p className="text-muted small mb-1">{t('prescriptions.notes')}</p>
                      <p className="mb-0">{selectedVisit.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Visit Images */}
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="fw-bold mb-3">{t('visits.visitImages')}</h5>
                {selectedVisit.files && selectedVisit.files.length > 0 ? (
                  <ImageGallery images={selectedVisit.files} />
                ) : (
                  <p className="text-muted text-center py-3 mb-0">
                    {t('visits.noImagesFound', 'لا توجد صور لهذه الزيارة')}
                  </p>
                )}
              </div>
            </div>

            {/* Prescriptions for this Visit */}
            <div data-prescriptions-section>
              <PrescriptionsList 
                visitId={selectedVisit.id} 
                onPrescriptionClick={(id) => {
                  setSelectedPrescriptionId(id);
                }} 
              />
              
              {/* Prescription Details - Show inline when selected */}
              {selectedPrescriptionId && (
                <div className="card shadow-sm border-0 mt-3" style={{ borderLeft: `4px solid #3b82f6 !important` }}>
                  <div className="card-body">
                    {isLoadingPrescription ? (
                      <div className="d-flex justify-content-center py-3">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : selectedPrescription ? (
                      <div className="space-y-4">
                        <div className={`d-flex align-items-center justify-content-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <h5 className="fw-bold mb-0">{t('prescriptions.prescriptionDetails')}</h5>
                          <Button
                            variant="text"
                            size="sm"
                            onClick={() => setSelectedPrescriptionId(null)}
                            className="text-muted"
                          >
                            {t('common.close')}
                          </Button>
                        </div>

                        {/* Prescription Information */}
                        <div className="row g-3 mb-3">
                          <div className="col-12 col-md-6">
                            <div className={`d-flex align-items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Calendar className="text-primary" size={18} />
                              <div>
                                <p className="text-muted small mb-1">{t('prescriptions.createdAt', 'تاريخ الإنشاء')}</p>
                                <p className="mb-0 fw-semibold">{formatDate(selectedPrescription.createdAt)}</p>
                              </div>
                            </div>
                          </div>
                          {selectedPrescription.diagnosis && (
                            <div className="col-12">
                              <div className={`d-flex align-items-start gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Stethoscope className="text-info mt-1" size={18} />
                                <div className="flex-grow-1">
                                  <p className="text-muted small mb-1">{t('prescriptions.diagnosis')}</p>
                                  <p className="mb-0 fw-semibold">{selectedPrescription.diagnosis}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Medications */}
                        {selectedPrescription.items && selectedPrescription.items.length > 0 && (
                          <div className="mb-3">
                            <div className={`d-flex align-items-center gap-2 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Pill className="text-success" size={20} />
                              <h6 className="fw-bold mb-0">{t('prescriptions.medications')}</h6>
                            </div>
                            <div className="list-group list-group-flush">
                              {selectedPrescription.items.map((item) => (
                                <div key={item.id} className="list-group-item border-0 border-bottom px-0 py-2">
                                  <PrescriptionItem item={item} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Lab Tests */}
                        {selectedPrescription.labTests && selectedPrescription.labTests.length > 0 && (
                          <div className="mb-3">
                            <div className={`d-flex align-items-center gap-2 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <FlaskConical className="text-info" size={20} />
                              <h6 className="fw-bold mb-0">{t('prescriptions.labTests', 'التحاليل')}</h6>
                            </div>
                            <ul className="list-group list-group-flush">
                              {selectedPrescription.labTests.map((test) => (
                                <li key={test.id} className="list-group-item border-0 border-bottom px-0 py-2">
                                  <p className="mb-0">{test.name}</p>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Radiology Tests */}
                        {selectedPrescription.radiologyTests && selectedPrescription.radiologyTests.length > 0 && (
                          <div className="mb-3">
                            <div className={`d-flex align-items-center gap-2 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Scan className="text-warning" size={20} />
                              <h6 className="fw-bold mb-0">{t('prescriptions.radiologyTests', 'الأشعة')}</h6>
                            </div>
                            <ul className="list-group list-group-flush">
                              {selectedPrescription.radiologyTests.map((test) => (
                                <li key={test.id} className="list-group-item border-0 border-bottom px-0 py-2">
                                  <p className="mb-0">{test.name}</p>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Notes */}
                        {selectedPrescription.notes && (
                          <div>
                            <div className={`d-flex align-items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <FileText className="text-secondary" size={20} />
                              <h6 className="fw-bold mb-0">{t('prescriptions.notes')}</h6>
                            </div>
                            <p className="mb-0">{selectedPrescription.notes}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <ErrorMessage message={t('prescriptions.prescriptionNotFound', 'الوصفة الطبية غير موجودة')} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <ErrorMessage message={t('visits.visitNotFound')} />
        )}
      </Modal>

      {/* Edit Patient Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={t('patients.editPatient', 'Edit Patient')}
      >
        <PatientForm
          patient={patient}
          onSubmit={handleUpdatePatient}
          isLoading={updatePatientMutation.isPending}
        />
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeletePatient}
        title={t('common.delete', 'Delete')}
        message={
          visits && visits.length > 0
            ? t('patients.deleteConfirmationWithVisits', 
                `Are you sure you want to delete this patient? This will also delete ${visits.length} visit(s) and all associated data (files, prescriptions). This action cannot be undone.`
              ).replace('{{count}}', visits.length.toString())
            : t('patients.deleteConfirmation', 'Are you sure you want to delete this patient? This action cannot be undone.')
        }
        confirmText={t('common.delete', 'Delete')}
        variant="danger"
      />
    </div>
  );
};
